import type * as React from "react";
import { Group, Provider, TextContext } from "react-aria-components";
import { icon } from "../icon.css.ts";
import { AlertTriangle, CheckCircle, InfoCircle } from "../icons/line.tsx";
import {
  action,
  alerts,
  defaultIcon,
  descriptionText,
  dismissButton,
  titleText,
} from "./Alert.css.ts";
import type { Hierarchy as ButtonHierarchy } from "./Button.tsx";
import { type CloseVariants, CloseX } from "./CloseX.tsx";
import type { Hierarchy as LinkColors } from "./Link.tsx";
import {
  type Actions,
  NotificationContent,
} from "./shared/NotificationContent.tsx";

type AlertType = keyof typeof alerts.classNames.variants.type;

export interface Props {
  /** Visual style indicating severity: `info`, `message`, `success`, `warning`, `error`, `primary`, or `secondPrimary`. */
  type: AlertType;
  /** Main heading text of the alert. */
  title: string;
  /**
   * Whether to show a colored border.
   * @default true
   */
  stroke?: boolean;
  /** Optional supporting text providing additional context. */
  description?: React.ReactNode;
  /** Callback when dismiss button is clicked. If not provided, alert is not dismissible. */
  onClose?: () => void;
  /** Array of action items (buttons or links) to display. */
  actions?: Actions;
  /** Custom icon component to replace the default type-based icon. */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Ref to the underlying div element. */
  ref?: React.Ref<HTMLDivElement>;
}

function renderIcon(type: AlertType) {
  switch (type) {
    case "info":
    case "message":
    case "error":
    case "primary":
    case "secondPrimary": {
      return <InfoCircle className={defaultIcon({ type, preset: true })} />;
    }
    case "warning": {
      return <AlertTriangle className={defaultIcon({ type, preset: true })} />;
    }
    case "success": {
      return <CheckCircle className={defaultIcon({ type, preset: true })} />;
    }
  }
}

const alertTypeToButtonColorMap: Record<AlertType, ButtonHierarchy> = {
  info: "secondaryGray",
  message: "secondaryGray",
  primary: "primary",
  secondPrimary: "secondaryColor",
  error: "alertErrorSecondary",
  warning: "alertWarningPrimary",
  success: "alertSuccessSecondary",
};

const alertTypeToLinkColorMap: Record<AlertType, LinkColors> = {
  info: "gray",
  message: "gray",
  primary: "primary",
  secondPrimary: "primary",
  error: "error",
  warning: "alert",
  success: "success",
};

const alertTypeToCloseVariant: Record<AlertType, CloseVariants> = {
  info: "gray",
  message: "gray",
  primary: "primary",
  secondPrimary: "primary",
  error: "error",
  warning: "alert",
  success: "success",
};

const defaultAction: Actions = [];

/**
 * Displays contextual feedback messages for user actions or system events. Supports different severity types, optional dismiss button, and action buttons or links.
 */
export const Alert: React.FC<Props> = ({
  type,
  title,
  description,
  onClose,
  stroke = true,
  actions = defaultAction,
  icon: Icon,
  ref,
}) => {
  const customIconDescriptionOnly =
    Icon != null && description == null && actions.length === 0;
  return (
    <Group className={alerts({ type, stroke })} ref={ref}>
      {Icon ? (
        <Group
          className={defaultIcon({
            type,
            descriptionOnly: customIconDescriptionOnly,
          })}
        >
          <Icon className={icon({ size: "m" })} />
        </Group>
      ) : (
        renderIcon(type)
      )}
      {/*
        An Alert renders into whatever context it is placed in, and a surrounding
        Dialog supplies a TextContext accepting only a "description" slot. Declare
        both slots here so the title is never validated against that outer set.
      */}
      <Provider
        values={[[TextContext, { slots: { title: {}, description: {} } }]]}
      >
        <NotificationContent
          title={title}
          titleClassName={titleText({ type })}
          titleAdornment={
            onClose && (
              <Group className={dismissButton}>
                <CloseX
                  size="s"
                  type="square"
                  variant={alertTypeToCloseVariant[type]}
                  onPress={onClose}
                />
              </Group>
            )
          }
          description={description}
          descriptionClassName={descriptionText({ type })}
          actions={actions}
          actionsClassName={action}
          buttonHierarchy={alertTypeToButtonColorMap[type]}
          linkHierarchy={alertTypeToLinkColorMap[type]}
        />
      </Provider>
    </Group>
  );
};
